import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { CheckCircle2, AlertCircle, Loader2, HardDrive, ArrowRight } from 'lucide-react';

import { useAuthStore } from '@/stores/authStore';

export const AuthCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Procesando autenticación...');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isDriveConnection, setIsDriveConnection] = useState(false);

  useEffect(() => {
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const state = searchParams.get('state');
    const type = searchParams.get('type');
    const isDrive = state === 'drive' || type === 'drive';
    setIsDriveConnection(isDrive);

    if (error) {
      setStatus('error');
      setMessage(`Acceso cancelado o denegado por Google: ${error}`);
      return;
    }

    if (!code) {
      setStatus('error');
      setMessage('No se encontró el código de autorización en la URL.');
      return;
    }

    const exchangeCode = async () => {
      try {
        const redirectUri = window.location.origin + '/auth/callback';

        if (isDrive) {
          // ─── Conectar Google Drive (Compartido para el equipo) ───
          const res = await api.handleGoogleDriveCallback(code, redirectUri);
          setStatus('success');
          setUserEmail(res.user?.email || 'Google Drive');
          setMessage('¡Google Drive conectado correctamente para el equipo!');

          setTimeout(() => {
            navigate('/personal', { replace: true });
          }, 1800);
        } else {
          // ─── Iniciar Sesión de Usuario (Login independiente) ───
          const res = await api.handleGoogleLoginCallback(code, redirectUri);
          const sessionToken = res.session_token || 'active';
          useAuthStore.getState().login(res.user, sessionToken);

          setStatus('success');
          setUserEmail(res.user?.email || 'tu cuenta de Google');
          setMessage('¡Sesión iniciada correctamente!');

          setTimeout(() => {
            navigate('/personal', { replace: true });
          }, 1200);
        }
      } catch (err: any) {
        console.error('Error al procesar callback de Google:', err);
        setStatus('error');
        setMessage(err.message || 'Error al autenticar con Google.');
      }
    };

    exchangeCode();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 p-8 text-center transition-all">
        {/* Ícono animado */}
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center bg-violet-50 text-violet-600 shadow-inner">
          {status === 'loading' && <Loader2 className="w-8 h-8 animate-spin text-violet-600" />}
          {status === 'success' && <CheckCircle2 className="w-8 h-8 text-emerald-500 animate-bounce" />}
          {status === 'error' && <AlertCircle className="w-8 h-8 text-rose-500" />}
        </div>

        {/* Título */}
        <h2 className="text-xl font-bold text-slate-900 mb-2">
          {status === 'loading' && (isDriveConnection ? 'Conectando Google Drive del equipo...' : 'Iniciando sesión...')}
          {status === 'success' && (isDriveConnection ? '¡Google Drive Vinculado!' : '¡Bienvenida de vuelta!')}
          {status === 'error' && 'No se pudo conectar'}
        </h2>

        {/* Mensaje descriptivo */}
        <p className="text-slate-600 text-sm mb-6 leading-relaxed">
          {status === 'loading' &&
            (isDriveConnection
              ? 'Guardando permisos para sincronizar las carpetas y archivos compartidos de tus clientes...'
              : 'Verificando tu cuenta de usuario...')}
          {status === 'success' &&
            (isDriveConnection ? (
              <span>
                La cuenta de Google Drive <strong className="text-slate-800">{userEmail}</strong> quedó vinculada para todo el equipo. Los archivos de clientes se mantendrán sincronizados sin importar quién inicie sesión.
              </span>
            ) : (
              <span>
                Iniciaste sesión como <strong className="text-slate-800">{userEmail}</strong>. Tu cuenta de Drive compartida sigue activa e intacta.
              </span>
            ))}
          {status === 'error' && message}
        </p>

        {/* Acciones */}
        {status === 'success' && (
          <div className="pt-2">
            <button
              onClick={() => navigate('/personal')}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white font-medium bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shadow-md shadow-violet-200 transition-all text-sm"
            >
              Ir al Panel Principal
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-3 pt-2">
            <button
              onClick={() => navigate('/')}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-slate-700 font-medium bg-slate-100 hover:bg-slate-200 transition-all text-sm"
            >
              Regresar al Inicio
            </button>
          </div>
        )}

        {/* Indicador de pie */}
        <div className="mt-8 pt-6 border-t border-slate-100 flex items-center justify-center gap-2 text-xs text-slate-400">
          <HardDrive className="w-3.5 h-3.5" />
          <span>{isDriveConnection ? 'Almacenamiento de Archivos en Google Drive' : 'Sesión Personal de Usuario'}</span>
        </div>
      </div>
    </div>
  );
};
